import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FileCheck2, Route, BedDouble, ShieldCheck, Check, X, ArrowRight } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const VISA_TYPES = [
  "Schengen Visa",
  "US Tourist Visa (B1/B2)",
  "Canadian Tourist Visa",
  "Singapore Tourist Visa",
  "Armenia Tourist Visa",
  "UK Tourist Visa",
  "Turkey Tourist Visa",
];

const SERVICES = [
  {
    key: "visa-forms",
    icon: FileCheck2,
    kicker: "Tourist visa",
    title: "Form Filling Assistance",
    body: "We fill the forms for the visas people actually travel on - accurately, completely and in the exact format each consulate expects.",
    span: "lg:col-span-7",
    chips: VISA_TYPES,
  },
  {
    key: "travel-itinerary",
    icon: Route,
    kicker: "Planning",
    title: "Travel Itinerary",
    body: "A well-planned travel itinerary makes your journey smoother, stress-free and more enjoyable. We create personalized itineraries tailored to your destination, interests, schedule and travel needs.",
    span: "lg:col-span-5",
  },
  {
    key: "hotels-booking",
    icon: BedDouble,
    kicker: "Booking",
    title: "Hotels",
    body: "We find and book the right rooms for your budget, location and amenities — including the confirmations your application needs.",
    span: "lg:col-span-5",
  },
  {
    key: "travel-insurance",
    icon: ShieldCheck,
    kicker: "Getting",
    title: "Travel Insurance",
    body: "We compare the major insurance providers and suggest the cover that fits your trip — and your consulate's requirements.",
    span: "lg:col-span-7",
  },
];

const WHAT_YOU_GET = [
  "Professional guidance",
  "Unparalleled visa form-filling experience",
  "A dedicated support agent",
  "Assistance with hotel booking, itinerary & travel insurance",
];

const SCHENGEN_DOCS = [
  { item: "Visa application form", note: "Fully completed with correct information, printed and signed at the end." },
  { item: "Two recent photos", note: "Taken within the last three months, in compliance with the Schengen visa photo criteria." },
  { item: "Valid passport", note: "No older than ten years, valid for at least three months beyond your planned stay, with at least two blank pages for the visa sticker." },
  { item: "Roundtrip reservation or itinerary", note: "A document with dates and flight numbers specifying entry and exit from the Schengen area." },
  { item: "Travel health insurance", note: "Evidence of health insurance covering medical emergencies for your whole period of stay." },
  { item: "Proof of accommodation", note: "Evidence of where you will be staying throughout your time in Schengen." },
  { item: "Letter of tour organizer", note: "If you will be travelling with a tour agency." },
  { item: "Proof of financial means", note: "A bank account statement, a sponsorship letter, or a combination of both — showing you can support yourself throughout your stay." },
  {
    item: "Evidence of employment status",
    children: [
      "Employed: employment contract, leave permission from the employer, Income Tax Return",
      "Self-employed: business license, company bank statement for the latest 6 months, Income Tax Return (ITR)",
      "Student: proof of enrollment & No Objection Letter from university",
    ],
  },
  { item: "Travel itinerary", note: "A description of your trip — your purpose, the places you will visit, the time frame and your personal data." },
  {
    item: "For minors",
    children: [
      "Birth certificate / proof of adoption / custody decree if parents are divorced / death certificate of a parent",
      "Letter of consent from parents, including passport copies of both parents or the legal guardian",
    ],
  },
];

export const Services = ({ onNavigate }) => {
  const [schengenOpen, setSchengenOpen] = useState(false);
  const [comingSoon, setComingSoon] = useState(null);

  return (
  <section
    id="products"
    data-testid="services-section"
    className="px-6 md:px-12 pt-28 md:pt-36"
  >
    <div className="max-w-[1400px] mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-8 mb-16">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: EASE }}
            className="text-xs font-semibold tracking-[0.25em] uppercase text-slate-500 mb-5"
          >
            Products
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
            className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900"
          >
            Everything between you and the{" "}
            <em className="italic text-slate-500">visa stamp.</em>
          </motion.h2>
        </div>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
          className="max-w-sm text-sm text-slate-600 leading-relaxed"
        >
          Four services, one goal: a complete, credible application. Take them
          separately or let a package bundle them for you below.
        </motion.p>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        {SERVICES.map((s, i) => (
          <motion.div
            key={s.key}
            data-testid={`service-card-${s.key}`}
            initial={{ opacity: 0, y: 48 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: (i % 2) * 0.12, ease: EASE }}
            className={`group bg-white border border-slate-200 hover:border-slate-400 p-10 md:p-12 transition-colors duration-500 ${s.span}`}
          >
            <span className="w-12 h-12 flex items-center justify-center border border-slate-200 rounded-full transition-colors duration-500 group-hover:border-slate-900 group-hover:bg-slate-900">
              <s.icon className="w-5 h-5 text-slate-700 transition-colors duration-500 group-hover:text-white" />
            </span>
            {s.kicker && (
              <p className="mt-8 text-xs font-semibold tracking-[0.25em] uppercase text-slate-400" data-testid={`service-kicker-${s.key}`}>
                {s.kicker}
              </p>
            )}
            <h3 className={`font-display text-2xl sm:text-3xl tracking-tight text-slate-900 ${s.kicker ? "mt-3" : "mt-8"}`}>
              {s.title}
            </h3>
            <p className="mt-4 text-sm sm:text-base text-slate-600 leading-relaxed max-w-lg">
              {s.body}
            </p>
            {s.chips && (
              <div className="mt-8 flex flex-wrap gap-2.5" data-testid="visa-type-chips">
                {s.chips.map((chip) =>
                  chip === "Schengen Visa" ? (
                    <button
                      key={chip}
                      data-testid="chip-schengen-visa"
                      onClick={() => setSchengenOpen((v) => !v)}
                      aria-expanded={schengenOpen}
                      className={`text-xs font-semibold px-4 py-2 rounded-full border transition-colors duration-300 ${
                        schengenOpen
                          ? "bg-slate-900 text-white border-slate-900"
                          : "text-slate-900 bg-white border-slate-300 hover:border-slate-900"
                      }`}
                    >
                      {chip}
                    </button>
                  ) : chip === "US Tourist Visa (B1/B2)" ? (
                    <Link
                      key={chip}
                      to="/us-tourist-visa"
                      data-testid="chip-us-tourist-visa"
                      className="text-xs font-semibold px-4 py-2 rounded-full border text-slate-900 bg-white border-slate-300 hover:border-slate-900 transition-colors duration-300"
                    >
                      {chip}
                    </Link>
                  ) : chip === "Armenia Tourist Visa" ? (
                    <Link
                      key={chip}
                      to="/armenia-visa"
                      data-testid="chip-armenia-tourist-visa"
                      className="text-xs font-semibold px-4 py-2 rounded-full border text-slate-900 bg-white border-slate-300 hover:border-slate-900 transition-colors duration-300"
                    >
                      {chip}
                    </Link>
                  ) : (
                    <button
                      key={chip}
                      data-testid={`chip-coming-soon-${chip.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      onClick={() => setComingSoon((v) => (v === chip ? null : chip))}
                      className={`text-xs font-semibold px-4 py-2 rounded-full border transition-colors duration-300 ${
                        comingSoon === chip
                          ? "bg-slate-900 text-white border-slate-900"
                          : "text-slate-600 bg-[#F3F3F1] border-slate-200 hover:border-slate-900 hover:text-slate-900"
                      }`}
                    >
                      {chip}
                    </button>
                  )
                )}
              </div>
            )}
          </motion.div>
        ))}
      </div>

      <AnimatePresence>
        {schengenOpen && (
          <motion.div
            id="schengen-visa"
            data-testid="schengen-details"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="overflow-hidden"
          >
            <div className="mt-6 bg-white border border-slate-200 p-10 md:p-14">
              <div className="flex items-start justify-between gap-6 pb-10 border-b border-slate-200">
                <div>
                  <p className="text-xs font-semibold tracking-[0.25em] uppercase text-slate-500 mb-4">
                    Visa guide
                  </p>
                  <h3 className="font-display text-3xl sm:text-4xl tracking-tight text-slate-900">
                    Schengen Visa
                  </h3>
                  <p className="mt-3 text-sm text-slate-500">
                    Tourist short-stay · up to 90 days within any 6-month period
                  </p>
                </div>
                <button
                  data-testid="schengen-close-button"
                  onClick={() => setSchengenOpen(false)}
                  aria-label="Close Schengen details"
                  className="w-10 h-10 shrink-0 flex items-center justify-center border border-slate-200 rounded-full text-slate-500 hover:border-slate-900 hover:text-slate-900 transition-colors duration-300"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="grid lg:grid-cols-2 gap-10 py-12 border-b border-slate-200">
                <div>
                  <h4 className="font-display text-xl sm:text-2xl text-slate-900">
                    Traveling to Europe for tourism
                  </h4>
                  <p className="mt-4 text-sm sm:text-base text-slate-600 leading-relaxed">
                    A Tourist Schengen Visa permits third-country nationals to
                    enter the Schengen Area for a maximum of 90 days within a
                    6-month period, for the purpose of travelling or visiting
                    Europe for pleasure.
                  </p>
                  <h4 className="font-display text-xl sm:text-2xl text-slate-900 mt-10">
                    Who needs one
                  </h4>
                  <p className="mt-4 text-sm sm:text-base text-slate-600 leading-relaxed">
                    Non-European citizens wishing to travel to Europe for
                    tourism may be required to apply for a short-term Schengen
                    visa under the "tourist" specification — if your nationality
                    is on the{" "}
                    <Link
                      to="/schengen-visa-countries"
                      data-testid="schengen-countries-link"
                      className="font-semibold text-slate-900 underline underline-offset-4 decoration-slate-300 hover:decoration-slate-900 transition-[text-decoration-color] duration-300"
                    >
                      list of countries whose nationals need a Schengen short-stay visa
                    </Link>
                    .
                  </p>
                </div>
                <div className="bg-[#F3F3F1] border border-slate-200 p-8 md:p-10 h-fit">
                  <p className="text-xs font-semibold tracking-[0.25em] uppercase text-slate-500 mb-6">
                    What you'll get
                  </p>
                  <ul className="space-y-4">
                    {WHAT_YOU_GET.map((g) => (
                      <li key={g} className="flex items-start gap-3 text-sm sm:text-base text-slate-700">
                        <Check className="w-4 h-4 mt-1 shrink-0 text-slate-900" />
                        {g}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="pt-12">
                <h4 className="font-display text-2xl sm:text-3xl tracking-tight text-slate-900">
                  Documents you'll need
                </h4>
                <div className="grid md:grid-cols-2 gap-x-14 mt-4">
                  {SCHENGEN_DOCS.map((d) => (
                    <div key={d.item} className="py-5 border-b border-slate-100" data-testid={`schengen-doc-${d.item.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                      <div className="flex items-start gap-3">
                        <Check className="w-4 h-4 mt-1 shrink-0 text-slate-900" />
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{d.item}</p>
                          {d.note && (
                            <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{d.note}</p>
                          )}
                          {d.children && (
                            <ul className="mt-3 space-y-2">
                              {d.children.map((c) => (
                                <li
                                  key={c}
                                  className="text-sm text-slate-500 leading-relaxed pl-4 relative before:content-[''] before:absolute before:left-0 before:top-[10px] before:w-1.5 before:h-px before:bg-slate-400"
                                >
                                  {c}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  data-testid="schengen-cta-button"
                  onClick={() => onNavigate("#contact")}
                  className="group mt-12 flex items-center gap-3 bg-slate-900 text-white text-sm font-semibold px-8 py-4 rounded-full hover:bg-slate-700 transition-colors duration-300"
                >
                  Start your Schengen file
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {comingSoon && (
          <motion.div
            data-testid="coming-soon-panel"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="overflow-hidden"
          >
            <div className="mt-6 bg-white border border-slate-200 p-10 md:p-12 flex flex-wrap items-center justify-between gap-8">
              <div>
                <p className="text-xs font-semibold tracking-[0.25em] uppercase text-slate-500 mb-3">
                  Coming soon
                </p>
                <p className="font-display text-2xl sm:text-3xl tracking-tight text-slate-900">
                  {comingSoon}
                </p>
                <p className="mt-3 text-sm text-slate-600 max-w-lg leading-relaxed">
                  We're putting the finishing touches on this guide. Send us
                  your interest and a specialist will reach out with the
                  details — or start your file today.
                </p>
              </div>
              <div className="flex items-center gap-4">
                <button
                  data-testid="coming-soon-interest-button"
                  onClick={() => onNavigate("#contact")}
                  className="group flex items-center gap-3 bg-slate-900 text-white text-sm font-semibold px-7 py-3.5 rounded-full hover:bg-slate-700 transition-colors duration-300"
                >
                  Send us your interest
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
                <button
                  data-testid="coming-soon-close-button"
                  onClick={() => setComingSoon(null)}
                  aria-label="Close coming soon note"
                  className="w-10 h-10 flex items-center justify-center border border-slate-200 rounded-full text-slate-500 hover:border-slate-900 hover:text-slate-900 transition-colors duration-300"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  </section>
  );
};

